/** College Match — from "who I am" to "where I apply". */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAdminRole } from "@/hooks/useAdminRole";
import { formatDateOnly } from "@/lib/collegeMatch";
import type { StudentPayload } from "@/components/report/reportTypes";
import type { Json } from "@/integrations/supabase/types";
import CollegePlanSection from "@/components/report/CollegePlanSection";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PageHead from "@/components/PageHead";
import { ArrowLeft, ArrowRight, ExternalLink, GraduationCap, Info, Loader2, Printer, Save } from "lucide-react";
import { toast } from "sonner";
import {
  matchColleges, buildCollegeRoadmap, validateCollegeInputs, type SavedCollegePlan, type MatchInputs, type MatchResult, type Program, type RoadmapStep,
} from "@/lib/collegeMatch";

const COUNTRIES = ["India", "United States", "United Kingdom", "Canada", "Australia"];
const EXAMS = ["JEE", "NEET", "CUET", "CLAT", "NID DAT", "UCEED", "SAT", "ACT", "IELTS", "TOEFL"];
const TIER_COPY: Record<MatchResult["tier"], { title: string; sub: string }> = {
  strong: { title: "Strong fit", sub: "A good fit and within reach today." },
  reach: { title: "Reach", sub: "A good fit, worth stretching for." },
  explore: { title: "Explore", sub: "Worth a look, even if it's above your budget." },
};

type Career = { name: string; cluster: string };

export default function CollegeMatch() {
  const { user } = useAuth();
  const admin = useAdminRole();
  const [params] = useSearchParams();
  const target = params.get("learner");
  const learnerId = target && admin.isAdmin ? target : user?.id;
  const [loadError, setLoadError] = useState("");
  const [learnerName, setLearnerName] = useState("");
  const [versions, setVersions] = useState<SavedCollegePlan[]>([]);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [explaining, setExplaining] = useState(false);
  const [loading, setLoading] = useState(true);
  const [careers, setCareers] = useState<Career[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [step, setStep] = useState(0);
  const [spec, setSpec] = useState<string[]>([]);
  const [specDraft, setSpecDraft] = useState("");
  const [countries, setCountries] = useState<string[]>(["India"]);
  const [cities, setCities] = useState("");
  const [marks, setMarks] = useState("");
  const [exams, setExams] = useState<Record<string, string>>({});
  const [budget, setBudget] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [results, setResults] = useState<MatchResult[] | null>(null);
  const [roadmap, setRoadmap] = useState<RoadmapStep[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || admin.loading) return;
    if (target && !admin.isAdmin) { setLoadError("Only counsellors can open another learner’s plan."); setLoading(false); return; }
    if (!learnerId) return;
    let live = true;
    (async () => {
      try {
      const [rep, progs, plan] = await Promise.all([
        supabase.from("learner_reports").select("payload").eq("learner_id", learnerId).eq("audience", "student")
          .order("generated_at", { ascending: false }).limit(1).maybeSingle(),
        supabase.from("college_programs").select("*, college:colleges(name,country,city,website), deadlines:program_deadlines(label,due_date,source_url)").eq("status", "published"),
        supabase.from("learner_college_plans").select("id,created_at,inputs,results,roadmap").eq("learner_id", learnerId).order("created_at", { ascending: false }).limit(100),
      ]);
      if (!live) return;
      if (rep.error || progs.error || plan.error) throw new Error("We couldn’t load your report or college list. Please try again.");
      const payload = rep.data?.payload as unknown as StudentPayload | null;
      setLearnerName(payload?.learner.display_name ?? "");
      const matches = payload?.career_matches ?? [];
      const top = matches.slice(0, 3).map((m) => ({ name: m.career_name, cluster: m.career_cluster }));
      setCareers(top);
      setSpec(top.map((t) => t.name));
      setPrograms((progs.data ?? []) as unknown as Program[]);
      const saved = (plan.data ?? []) as unknown as SavedCollegePlan[];
      setVersions(saved);
      const prev = saved[0]?.inputs;
      if (prev) {
        setCountries(prev.countries ?? ["India"]); setCities((prev.cities ?? []).join(", "));
        setMarks(prev.marksPercent?.toString() ?? ""); setBudget(prev.budgetPerYear?.toString() ?? "");
        setCurrency(prev.currency ?? "INR");
        setExams(Object.fromEntries(Object.entries(prev.examScores ?? {}).map(([k, v]) => [k, String(v)])));
        if (prev.specialisations?.length) setSpec(prev.specialisations);
      }
      if (target && user.id !== learnerId) await supabase.from("report_access_log").insert({ admin_id: user.id, learner_id: learnerId, action: "view" });
      } catch (e) { if (live) setLoadError(e instanceof Error ? e.message : "College Match could not load."); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [user?.id, learnerId, admin.loading, admin.isAdmin, target, historyRefresh]);

  const inputs: MatchInputs = useMemo(() => ({
    careers, specialisations: spec, countries,
    cities: cities.split(",").map((c) => c.trim()).filter(Boolean),
    marksPercent: marks ? Number(marks) : null,
    examScores: Object.fromEntries(Object.entries(exams).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)])),
    budgetPerYear: budget ? Number(budget) : null, currency,
  }), [careers, spec, countries, cities, marks, exams, budget, currency]);

  const run = () => {
    const problem = validateCollegeInputs(inputs);
    if (problem) { toast.error(problem); return; }
    const r = matchColleges(programs, inputs);
    setResults(r); setRoadmap(buildCollegeRoadmap(r)); setStep(4);
  };

  const save = async () => {
    if (!user || !learnerId || !results?.length) return;
    setSaving(true);
    const { error } = await supabase.from("learner_college_plans").insert({
      learner_id: learnerId, inputs: inputs as unknown as Json,
      results: results.map((r) => ({ program_id: r.program.id, college: r.program.college.name, course: r.program.course, tier: r.tier, reasons: r.reasons, flags: r.flags, program: r.program, confidence: r.confidence, aboveBudget: r.aboveBudget, raiseConfidence: r.raiseConfidence })) as unknown as Json,
      roadmap: roadmap as unknown as Json,
    });
    setSaving(false);
    if (error) toast.error("We couldn't save your plan. Please try again.");
    else { toast.success("Your college plan is saved. Each save keeps a new version."); setHistoryRefresh(n => n + 1); }
  };

  const explain = async () => {
    if (!results?.length || !learnerId) return;
    setExplaining(true);
    try {
      const { data, error } = await supabase.functions.invoke("college-explain", { body: { learner_id: learnerId, program_ids: results.map(r => r.program.id) } });
      if (error) throw new Error(error instanceof FunctionsHttpError ? (await error.context.json()).error ?? "AI explanations could not load." : error.message);
      setResults(results.map(r => ({ ...r, reasons: data.explanations?.find((e: { program_id: string; explanation: string }) => e.program_id === r.program.id)?.explanation ? [...r.reasons, data.explanations.find((e: { program_id: string }) => e.program_id === r.program.id).explanation] : r.reasons })));
    } catch (e) { toast.error(e instanceof Error ? e.message : "AI explanations could not load."); }
    finally { setExplaining(false); }
  };

  if (loadError) return <main className="mx-auto max-w-3xl p-8"><h1 className="font-display text-2xl">College Match</h1><p role="alert" className="my-4">{loadError}</p><Button asChild variant="outline"><Link to="/report">Back to report</Link></Button></main>;
  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" aria-label="Loading" /></div>;

  const toggle = (list: string[], v: string, set: (x: string[]) => void) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const Chip = ({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) => (
    <Button type="button" variant="outline" aria-pressed={on} onClick={onClick}
      className={`min-h-11 rounded-full border px-4 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:border-primary"}`}>{label}</Button>
  );

  const cards = [
    { title: "What do you want to study?", hint: "We've started with your top matches. Tap one to remove it, or add your own.", body: (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">{spec.map((s) => <Chip key={s} on label={`${s} ×`} onClick={() => setSpec(spec.filter((x) => x !== s))} />)}</div>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (specDraft.trim()) { setSpec([...spec, specDraft.trim()]); setSpecDraft(""); } }}>
          <Input className="h-12" placeholder="e.g. Game design, Robotics" value={specDraft} onChange={(e) => setSpecDraft(e.target.value)} aria-label="Add a specialisation" />
          <Button type="submit" variant="outline" className="h-12">Add</Button>
        </form>
      </div>) },
    { title: "Where would you like to study?", hint: "Pick one or more countries. Cities are optional.", body: (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">{COUNTRIES.map((c) => <Chip key={c} on={countries.includes(c)} label={c} onClick={() => toggle(countries, c, setCountries)} />)}</div>
        <Input className="h-12" placeholder="Preferred cities, separated by commas" value={cities} onChange={(e) => setCities(e.target.value)} aria-label="Preferred cities" />
      </div>) },
    { title: "Your marks and exam scores", hint: "All optional. Adding them sharpens your Reach and Strong fit tiers.", body: (
      <div className="space-y-4">
        <label className="block text-sm font-medium">Latest marks (%)
          <Input className="mt-2 h-12" inputMode="decimal" value={marks} onChange={(e) => setMarks(e.target.value)} placeholder="e.g. 82" /></label>
        <div className="grid grid-cols-2 gap-3">{EXAMS.map((e) => (
          <label key={e} className="text-sm">{e}
            <Input className="mt-1 h-12" inputMode="decimal" value={exams[e] ?? ""} onChange={(ev) => setExams({ ...exams, [e]: ev.target.value })} placeholder="Score" /></label>))}</div>
      </div>) },
    { title: "Your yearly budget", hint: "Tuition per year. We'll still show options above it, clearly labelled.", body: (
      <div className="flex gap-2">
        <select aria-label="Currency" className="h-12 rounded-md border border-input bg-background px-3" value={currency} onChange={(e) => setCurrency(e.target.value)}>
          {["INR", "USD", "GBP", "CAD", "AUD"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <Input className="h-12" inputMode="numeric" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="e.g. 300000" aria-label="Budget per year" />
      </div>) },
  ];

  return (
    <main className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
      <PageHead path="/colleges" title="College Match | WeKIT Career Clarity 360" description="Find colleges and courses that fit your Career Clarity 360 results, with a dated roadmap." />
      <Link to={target ? "/admin/reports" : "/report"} className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground print:hidden"><ArrowLeft className="h-4 w-4" />Back to your report</Link>
      <header className="mb-8">
        {target && <p className="mb-4 text-sm text-primary">Counsellor view · {learnerName || "Learner"}</p>}
        <p className="mb-2 flex items-center gap-2 text-sm font-medium text-primary"><GraduationCap className="h-4 w-4" />College Match</p>
        <h1 className="font-heading text-3xl font-semibold leading-tight sm:text-4xl">Where you could study what fits you</h1>
        {careers.length ? <p className="mt-3 text-muted-foreground">Built from your top matches: {careers.map((c) => c.name).join(", ")}.</p>
          : <p className="mt-3 text-muted-foreground">Finish your assessment to get matches built from your report. You can still add what you want to study below.</p>}
      </header>

      {versions.length > 0 && <div className="mb-6 print:hidden"><CollegePlanSection key={historyRefresh} learnerId={learnerId ?? ""} editable={!!target} /></div>}
      {step < 4 && (
        <Card className="rounded-3xl print:hidden">
          <CardContent className="space-y-6 p-6 sm:p-8">
            <p className="text-sm text-muted-foreground">Step {step + 1} of 4</p>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${((step + 1) / 4) * 100}%` }} /></div>
            <div>
              <h2 className="font-heading text-2xl font-semibold">{cards[step].title}</h2>
              <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground"><Info className="mt-0.5 h-4 w-4 shrink-0" />{cards[step].hint}</p>
            </div>
            {cards[step].body}
            <div className="flex justify-between gap-3">
              <Button variant="ghost" className="h-12" disabled={step === 0} onClick={() => setStep(step - 1)}>Back</Button>
              {step < 3 ? <Button className="h-12 rounded-2xl" onClick={() => setStep(step + 1)}>Continue<ArrowRight className="ml-2 h-4 w-4" /></Button>
                : <Button className="h-12 rounded-2xl" disabled={!countries.length} onClick={run}>Find my colleges</Button>}
            </div>
          </CardContent>
        </Card>
      )}

      {step === 4 && results && (
        <div className="space-y-10">
          <div className="flex flex-wrap gap-3 print:hidden">
            <Button variant="outline" className="h-12" onClick={() => setStep(0)}>Change my answers</Button>
            <Button variant="outline" className="h-12" disabled={saving || !results.length} onClick={save}><Save className="mr-2 h-4 w-4" />Save my plan</Button>
            <Button variant="outline" className="h-12" disabled={explaining || !results.length} onClick={explain}>{explaining ? "Writing…" : "Add AI perspective"}</Button>
            <Button variant="outline" className="h-12" onClick={() => window.print()}><Printer className="mr-2 h-4 w-4" />Print</Button>
          </div>

          {results.length === 0 ? (
            <Card className="rounded-3xl"><CardContent className="p-8">
              <h2 className="font-heading text-xl font-semibold">We're still building our college list for these choices</h2>
              <p className="mt-2 text-muted-foreground">Try adding another country or a broader specialisation. Your counsellor can add verified courses to the college list.</p>
            </CardContent></Card>
          ) : (["strong", "reach", "explore"] as const).map((tier) => {
            const list = results.filter((r) => r.tier === tier);
            if (!list.length) return null;
            return (
              <section key={tier} aria-labelledby={`tier-${tier}`}>
                <h2 id={`tier-${tier}`} className="font-heading text-2xl font-semibold">{TIER_COPY[tier].title}</h2>
                <p className="mb-4 text-muted-foreground">{TIER_COPY[tier].sub}</p>
                <div className="space-y-4">{list.map((r) => <ResultCard key={r.program.id} r={r} />)}</div>
              </section>
            );
          })}

          <section aria-labelledby="roadmap-h">
            <h2 id="roadmap-h" className="font-heading text-2xl font-semibold">Your roadmap to application day</h2>
            <p className="mb-4 text-muted-foreground">Dates come from official sources. Where a date isn't announced yet, we say so.</p>
            <ol className="relative space-y-4 border-l-2 border-primary/30 pl-6">
              {roadmap.map((s, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full bg-primary" aria-hidden="true" />
                  <p className="text-sm font-medium text-primary">{s.date ? formatDateOnly(s.date, { day: "numeric", month: "short", year: "numeric" }) : "Date not yet announced"}</p>
                  <p className="font-medium">{s.title}</p>
                  <p className="text-sm text-muted-foreground">{s.detail}</p>
                  {s.source && <a href={s.source} target="_blank" rel="noreferrer" className="text-sm text-primary underline">Official source</a>}
                </li>))}
            </ol>
          </section>
          <p className="text-sm text-muted-foreground">These are possibilities for today, not a fixed path. As you grow, your options grow too.</p>
        </div>
      )}
    </main>
  );
}

function ResultCard({ r }: { r: MatchResult }) {
  const p = r.program;
  const next = p.deadlines.filter((d) => d.due_date && d.due_date >= new Date().toISOString().slice(0, 10)).sort((a, b) => (a.due_date ?? "").localeCompare(b.due_date ?? ""))[0];
  return (
    <Card className="rounded-3xl break-inside-avoid">
      <CardContent className="space-y-3 p-6">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-heading text-lg font-semibold">{p.course}</h3>
            <p className="text-muted-foreground">{p.college.name} · {[p.college.city, p.college.country].filter(Boolean).join(", ")}</p>
          </div>
          <Badge variant="outline">Confidence: {r.confidence}</Badge>
        </div>
        <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
          <div><dt className="text-muted-foreground">Fees / year</dt><dd>{p.fees_per_year != null ? `${p.currency ?? ""} ${p.fees_per_year.toLocaleString()}` : "Not verified"}{r.aboveBudget && " · above your budget"}</dd></div>
          <div><dt className="text-muted-foreground">Entrance</dt><dd>{p.entrance_exams.join(", ") || "Check official site"}</dd></div>
          <div><dt className="text-muted-foreground">Next deadline</dt><dd>{next ? `${next.label}, ${formatDateOnly(next.due_date ?? "")}` : "Check official site"}</dd></div>
        </dl>
        <div>
          <p className="text-sm font-medium">Why this fits you</p>
          <ul className="list-disc pl-5 text-sm text-muted-foreground">{r.reasons.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
        {r.raiseConfidence && <p className="text-xs text-muted-foreground">{r.raiseConfidence}</p>}
        {r.flags.length > 0 && <p className="text-sm text-muted-foreground">{r.flags.join(" · ")}</p>}
        <div className="flex flex-wrap gap-3 text-sm">
          {p.source_url && <a href={p.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline">Official page<ExternalLink className="h-3 w-3" /></a>}
          {p.verified_at && <span className="text-muted-foreground">Last checked {formatDateOnly(String(p.verified_at).slice(0, 10))}</span>}
        </div>
      </CardContent>
    </Card>
  );
}
